import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { paymentService } from '../../services/paymentService';
import ErrorMessage from '../common/ErrorMessage';
import { formatCurrency } from '../../utils/helpers';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States (Stripe)', currency: 'USD', gateway: 'stripe' },
  { code: 'IN', label: 'India (Razorpay)', currency: 'INR', gateway: 'razorpay' },
  { code: 'CA', label: 'Canada (Stripe)', currency: 'CAD', gateway: 'stripe' },
  { code: 'GB', label: 'United Kingdom (Stripe)', currency: 'GBP', gateway: 'stripe' },
  { code: 'AU', label: 'Australia (Stripe)', currency: 'AUD', gateway: 'stripe' },
];

const getCountryOption = (code) => COUNTRY_OPTIONS.find(opt => opt.code === code) || COUNTRY_OPTIONS[0];

const Checkout = ({ planData, customerData, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentGateway, setPaymentGateway] = useState('stripe');
  const [userCountry, setUserCountry] = useState({ countryCode: 'US', currency: 'USD' });
  const userSelectedCountry = useRef(false);

  // Only run detection if user hasn't selected a country
  useEffect(() => {
    if (!userSelectedCountry.current) {
      const detectCountry = async () => {
        const detected = await paymentService.detectUserCountry();
        const option = getCountryOption(detected);
        setUserCountry({ countryCode: option.code, currency: option.currency });
        setPaymentGateway(option.gateway);
        console.log('[Auto-detect] Set country:', option);
      };
      detectCountry();
    }
  }, []);

  const handleCountryChange = (e) => {
    const code = e.target.value;
    const option = getCountryOption(code);
    setUserCountry({ countryCode: option.code, currency: option.currency });
    setPaymentGateway(option.gateway);
    paymentService.setUserCountry(option.code);
    userSelectedCountry.current = true;
    console.log('[User select] Set country:', option);
  };

  useEffect(() => {
    console.log('Render: userCountry', userCountry, 'paymentGateway', paymentGateway);
  }, [userCountry, paymentGateway]);

  const handleStripePayment = async () => {
    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const sessionResult = await paymentService.createPaymentSession(
        { ...planData, currency: userCountry.currency },
        { ...customerData, country_code: userCountry.countryCode, currency_code: userCountry.currency }
      );
      if (!sessionResult.success) {
        throw new Error(sessionResult.error || 'Failed to create payment session');
      }
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        sessionResult.data.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: customerData.contact_name,
              email: customerData.contact_email,
            },
          },
        }
      );
      if (stripeError) {
        throw new Error(stripeError.message);
      }
      if (paymentIntent.status === 'succeeded') {
        // Call success callback immediately without setting local success state
        onSuccess && onSuccess(paymentIntent);
      } else {
        throw new Error('Payment failed');
      }
    } catch (err) {
      setError(err.message);
      onError && onError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRazorpayPayment = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const orderResult = await paymentService.createPaymentSession(
        { ...planData, currency: userCountry.currency },
        { ...customerData, country_code: userCountry.countryCode, currency_code: userCountry.currency }
      );
      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create payment order');
      }
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderResult.data.amount,
        currency: orderResult.data.currency,
        name: 'SalesPinch',
        description: `${planData.plan_name} Plan`,
        order_id: orderResult.data.order_id,
        locale: 'en_IN',
        config: {
          display: {
            blocks: {
              banks: {
                name: "Pay using UPI",
                instruments: [
                  { method: "card", issuers: ["HDFC", "ICICI", "SBI"] },
                  { method: "netbanking", banks: ["HDFC", "ICICI", "SBI"] },
                  { method: "wallet", wallets: ["paytm", "phonepe"] }
                ]
              }
            }
          }
        },
        handler: async (response) => {
          try {
            const verifyResult = await paymentService.processRazorpayPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );
            if (verifyResult.success) {
              // Call success callback immediately without setting local success state
              onSuccess && onSuccess(verifyResult.data);
            } else {
              throw new Error(verifyResult.error || 'Payment verification failed');
            }
          } catch (err) {
            setError(err.message);
            onError && onError(err.message);
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: customerData.contact_name,
          email: customerData.contact_email,
          contact: customerData.contact_phone,
        },
        theme: {
          color: '#3399cc',
        },
        modal: {
          ondismiss: function() {
            setIsLoading(false);
          }
        },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      setError(err.message);
      onError && onError(err.message);
      setIsLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (paymentGateway === 'stripe') {
      await handleStripePayment();
    } else if (paymentGateway === 'razorpay') {
      await handleRazorpayPayment();
    }
  };

  return (
    <div className="checkout-container">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">Complete Your Purchase</h4>
            </div>
            <div className="card-body">
              {/* Plan Summary */}
              <div className="plan-summary mb-4 p-3 bg-light rounded">
                <h5>{planData.plan_name}</h5>
                <p className="mb-2">
                  <strong>Price:</strong> {formatCurrency(planData.base_price, userCountry.currency || 'USD')}/{planData.billing_cycle === 'Yearly' ? 'year' : 'month'}
                </p>
                <p className="mb-2">
                  <strong>Billing Cycle:</strong> {planData.billing_cycle || 'Monthly'}
                </p>
                <p className="mb-2">
                  <strong>Users:</strong> Up to {planData.user_limit}
                </p>
                <p className="mb-0">
                  <strong>AI Tokens:</strong> {planData.ai_tokens_limit.toLocaleString()}/{planData.billing_cycle === 'Yearly' ? 'year' : 'month'}
                </p>
              </div>

              {/* Country Selection */}
              <div className="form-group mb-4">
                <label htmlFor="country">Select your country:</label>
                <select
                  id="country"
                  className="form-control"
                  value={userCountry.countryCode}
                  onChange={handleCountryChange}
                >
                  {COUNTRY_OPTIONS.map(opt => (
                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Payment gateway will be selected automatically based on your country.
                </small>
              </div>

              {/* Payment Gateway Info */}
              <div className="payment-gateway-info mb-4 p-3 bg-info text-white rounded">
                <h6>Payment Gateway: {paymentGateway === 'razorpay' ? 'Razorpay' : 'Stripe'}</h6>
                <small>
                  {paymentGateway === 'razorpay' 
                    ? 'You will be redirected to Razorpay for secure payment processing.'
                    : 'Your payment will be processed securely by Stripe.'
                  }
                </small>
              </div>

              {/* Error Message */}
              {error && (
                <ErrorMessage error={error} />
              )}

              {/* Stripe Card Element */}
              {paymentGateway === 'stripe' && (
                <div className="form-group mb-4">
                  <label>Card Details:</label>
                  <div className="border rounded p-3">
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePayment}
                  disabled={isLoading || !paymentGateway}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    `Pay ${formatCurrency(planData.base_price, userCountry.currency || 'USD')}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 